<template>
  <with-loader class="flex flex-col h-full pt-4 overflow-visible"
               id="list-section"
               :loading="loaders.items"
               trigger-loader-on-mount
  >
    <div v-if="!isEmpty(items)" class="flex justify-between items-center w-full h-12 pl-5 pr-5 relative">
      <search-input :value="searchQuery" @input="handleSearchInput" />

      <dropdown-button v-if="addButtonOptions && !isNewItem && !isAddButtonDisabled"
        :name="addTooltipText"
        :options="addButtonOptions"
        :close-on-select="true"
        class="ml-2"
        primary-button-classes="h-12 w-12 rounded"
        :primary-button-variant="isNewItem ? 'primary-light-error' : 'primary-light'"
        @select-button="handleNewItem"
      >
        <template #label>
          <vs-icon class="border-primary" icon="add" color="primary" size="1.6rem"/>
        </template>
      </dropdown-button>
      <base-tooltip-button v-else
        id="new-item-button"
        :variant="isNewItem ? 'primary-light-error' : 'primary-light'"
        class="ml-2"
        button-classes="h-12 w-12"
        :tooltip-text="isAddButtonDisabled ? addTooltipDisabledText : addTooltipText"
        :is-disabled="isAddButtonDisabled"
        @click="isNewItem ? cancelNewItem() : handleNewItem()"
      >
        <vs-icon class="border-primary"
          :icon="isNewItem ? 'close' : 'add'"
          :color="isNewItem ? 'danger' : 'primary'"
          size="1.6rem"
        />
      </base-tooltip-button>
    </div>

    <side-list v-if="!isEmpty(filteredItems)"
               id="test-list-section"
               ref="sideList"
               :items="filteredItems"
               :selected-id="selectedId"
               :list-classes="listClasses"
               id-key="id"
               class="h-5/6"
               @item-selected="$emit('item-selected', $event)"
    >
      <template #item="{ item, isItemSelected }">
        <list-section-item :is-item-selected="isItemSelected"
                           :route-props="routeProps"
                           :item="item"
                           :is-answer="isAnswer"
                           :is-new-item="isNewItem"
                           :is-loading="(loaders.name && isItemBeingRenamed(item.id)) || loaders.conversationJsons"
                           :bot-id="botId"
                           :is-rename-mode="isItemBeingRenamed(item.id)"
                           :selected-id="selectedId"
                           :menu-builder="menuBuilder"
                           @set-rename-mode="onRenameStart"
                           @show="$emit('show', $event)"
                           @rename="onRenameSubmit"
                           @request-delete="$emit('request-delete', $event)"
                           @request-inline-and-delete="$emit('request-inline-and-delete', $event)"
                           @copy-code="$emit('copy-code', $event)"
                           @enable-auto-translate="$emit('enable-auto-translate', $event)"
        />
      </template>
    </side-list>

    <list-section-empty v-else-if="!loaders.items"
                        :search-query="searchQuery"
                        :name="routeProps.name"
    >
      <slot name="empty"/>
      <template #button>
        <dropdown-button v-if="addButtonOptions && !isAddButtonDisabled"
                         :name="addTooltipText"
                         :label="`<span class='capitalize'>Create New ${dropLast(1, routeProps.name)}</span>`"
                         :options="addButtonOptions"
                         :close-on-select="true"
                         primary-button-classes="w-48"
                         dropdown-position="above-left"
                         primary-button-variant="transparent-primary-bordered"
                         arrow-color="primary"
                         @select-button="handleNewItem"
        />

        <base-button v-else
                     variant="transparent-primary-bordered"
                     class="w-48 capitalize"
                     :disabled="isAddButtonDisabled"
                     @click="handleNewItem"
        >
          <span class="text-center w-full">Create New {{ dropLast(1, routeProps.name) }}</span>
        </base-button>
      </template>
    </list-section-empty>
  </with-loader>
</template>

<script>
import WithLoader from '@/components/common/WithLoader'
import DropdownButton from '@/components/common/DropdownButton'
import SideList from '@/components/common/SideList'
import SideListMenu from '@/components/common/SideListMenu'
import SearchInput from '../base-components/SearchInput.vue'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import ListSectionItem from '@/components/common/ListSectionItem'
import ListSectionEmpty from '@/components/common/ListSectionEmpty'
import Template from '@/components/templates/Template'
import dropLast from 'ramda/src/dropLast'
import { ITEM_IDS } from '@/utils/editor.constants'
import sanitizeString from 'replace-special-characters'

export default {
  name: 'ListSection',

  components: {
    Template,
    WithLoader,
    DropdownButton,
    SideList,
    SideListMenu,
    SearchInput,
    ListSectionItem,
    ListSectionEmpty
  },

  props: {
    botId: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    addButtonOptions: {
      type: Array,
      default: null
    },
    loaders: {
      type: Object,
      default: () => {}
    },
    isRenameMode: {
      type: Boolean,
      default: false
    },
    selectedId: {
      type: String,
      default: ''
    },
    addTooltipText: {
      type: String,
      required: true
    },
    addTooltipDisabledText: {
      type: String,
      default: ''
    },
    routeProps: {
      type: Object,
      required: true
    },
    isDeleteDialogShown: {
      type: Boolean,
      default: false
    },
    menuBuilder: {
      type: Function,
      default: () => {}
    },
    renamedId: {
      type: String,
      default: null
    },
    isAddButtonDisabled: {
      type: Boolean,
      default: false
    }
  },

  watch: {
    'loaders.items': {
      immediate: true,
      async handler(isLoading) {
        if (isLoading) return

        setTimeout(() => {
          this.handleNavigationMovement()
        }, 500)
      }
    },

    selectedId: {
      immediate: false,
      async handler() {
        this.handleNavigationMovement()
      }
    }
  },

  data() {
    return {
      itemNames: {},
      searchQuery: ''
    }
  },

  computed: {
    filteredItems() {
      if (isEmpty(this.searchQuery)) return this.items

      const term = sanitizeString(this.searchQuery.toLowerCase())
      return this.items.filter(item => {
        const name = sanitizeString(item.name.toLowerCase())
        return name.includes(term)
      })
    },

    isAnswer() {
      return !!head(this.items)?.intent
    },

    listClasses() {
      return this.isAnswer ? 'flex items-center' : ''
    },

    sideListEl() {
      return this.$refs.sideList?.$el
    },

    selectedSideListEl() {
      // to get side-list-item element, adding ref prop to router-link inside slot is causing
      // list to scroll to wrong position
      return this.sideListEl?.querySelector(
        `#id${this.selectedId}`
      )
    },

    isNewItem() {
      return this.selectedId === ITEM_IDS.NEW
    }
  },

  methods: {
    dropLast,
    isEmpty,

    handleSearchInput(searchQuery = '') {
      this.searchQuery = searchQuery
    },

    async handleNavigationMovement() {
      if (!this.sideListEl || !this.selectedSideListEl) return

      const { clientHeight } = this.sideListEl
      const bounding = this.selectedSideListEl.getBoundingClientRect()
      if (bounding.top >= clientHeight) {
        this.scrollToSelectedConversation()
      } else if (bounding.top - this.selectedSideListEl.clientHeight <= 0) {
        this.scrollToSelectedConversation()
      }
    },

    async scrollToSelectedConversation() {
      await this.$nextTick()
      if (!this.selectedSideListEl) return

      this.selectedSideListEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' })
    },

    handleNewItem(optionIndex) {
      if (this.items.some(({ id }) => id === ITEM_IDS.NEW)) return

      this.$emit('add', optionIndex)
      this.handleSearchInput()
    },

    cancelNewItem() {
      if (!this.items.some(({ id }) => id === ITEM_IDS.NEW)) return

      this.$emit('cancel')
    },

    onRenameStart(item) {
      this.$emit('set-rename-mode', item)
    },

    isItemBeingRenamed(id) {
      return id === this.renamedId
    },

    onRenameSubmit(item) {
      this.$emit('rename', item)
    }
  }
}
</script>
